// The app-wide preview engine (SI-4): sounds, a session's layer rows, the
// buy sheet's preview and Discover's mixdowns all play through here, one
// at a time. It ports mixins/loopAudio.js:
//   - one streaming <audio> element, created lazily
//   - buffering and the loaded fraction
//   - requestAnimationFrame progress, with a timeupdate fallback
//   - the metadata-duration fallback for chunked streams (Infinity until EOF)
//   - the Safari webm -> aac demote and retry
//   - abort-on-teardown, so the server stops transcoding abandoned plays
//   - a seamless loop: once the sound's padding-free file has decoded, Web
//     Audio takes over at the stream's next wrap (audio/gaplessLoop.js)
//
//   play(item, { src, loop = true, durationHint, queue, index })
//   playSound(sound, { queue, index, cta, loop = true, color })
//   playUrl(url, item, { fallbackUrl, loop = true, gapless })
//   toggle, pause, resume, stop, seek(frac), next, prev
//   isPlaying(id), isBuffering(id), progressOf(id), loadedOf(id)
//
// Previews loop by default. Starting one pauses a session's Play all
// (audio/nowPlaying.js), which stays resumable from the bar.
import { reactive } from "vue";
import store from "@/store";
import { demoteStreamFormat, getStreamUrlFromProxyWithId, getWavDownloadFromProxyWithId } from "@/filters/utils";
import { forName } from "@/kit/people.js";
import { canBuy, canDownload, keyBpmText, soundCta } from "@/kit/sound.js";
import { addToSession as stageInNewSession, canAddToSession } from "@/services/addToSession.js";
import { download as downloadSound, shareLink } from "@/services/soundActions.js";
import { soundSheets } from "@/shell/soundSheets.js";
import { prettySessionName } from "@/kit/sessionName.js";
import nowPlaying from "./nowPlaying.js";
import { canStep, stepTarget } from "./queueStep.js";
import { GaplessLoop, MAX_GAPLESS_SECONDS } from "./gaplessLoop.js";

const st = reactive({
  id: null,
  item: null,
  src: null,
  playing: false,
  buffering: false,
  progress: 0, // 0-100
  loaded: 0, // 0-100
  loop: true,
  queue: [],
  index: -1,
  error: null,
});

let audio = null;
let metaDuration = 0;
let fallbackUrl = null;
let raf = null;
let durationSeq = 0;
// Bumped by every start: the old play()'s rejection (aborted when the next
// sound took the element) never clears the new one's loading ring (L3).
let srcSeq = 0;
// The queue's colour per sound (a session's layer rows): ⏮ ⏭ keep the
// row colours (L5).
let queueColorFor = null;

const API = process.env.VUE_APP_API_URL || "";

// The seamless loop (gaplessLoop.js). `owner` is who sounds: the streaming
// element until the takeover, then the decoded buffer. gaplessUrl is the
// current item's padding-free file, kept so a retried stream asks again.
let context = null;
let gapless = null;
let gaplessUrl = null;
let owner = "element";
let handoffTimer = null;
let handoffPending = false;
let quietPause = false; // our own pause of the element at the takeover
let onVisible = null;

function audioContext() {
  if (context) return context;
  const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  try {
    context = new AC();
  } catch (e) {
    context = null;
  }
  return context;
}

function gaplessEngine() {
  const ctx = audioContext();
  if (!ctx) return null;
  if (!gapless) {
    gapless = new GaplessLoop({
      context: ctx,
      fetchArrayBuffer: (url, { signal } = {}) =>
        fetch(url, { signal }).then((r) => {
          if (!r.ok) throw new Error(`gapless ${r.status}`);
          return r.arrayBuffer();
        }),
    });
    gapless.onEnded = passEnded;
  }
  return gapless;
}

const bufferOwns = () => owner === "buffer" && !!gapless;

function effectiveDuration() {
  if (bufferOwns()) return gapless.duration;
  if (audio && isFinite(audio.duration) && audio.duration > 0) return audio.duration;
  return metaDuration || 0;
}

function updateLoaded() {
  const d = effectiveDuration();
  if (!audio || d <= 0 || !audio.buffered || audio.buffered.length === 0) return;
  const end = audio.buffered.end(audio.buffered.length - 1);
  st.loaded = Math.min((end / d) * 100, 100);
}

function updateProgress() {
  if (bufferOwns()) {
    st.progress = Math.min(gapless.phase() * 100, 100);
    st.loaded = 100;
    return;
  }
  const d = effectiveDuration();
  if (!audio || d <= 0) return;
  st.progress = Math.min(((audio.currentTime % d) / d) * 100, 100);
  updateLoaded();
}

function startLoop() {
  if (raf || typeof requestAnimationFrame !== "function") return;
  const tick = () => {
    raf = null;
    const running = bufferOwns() ? gapless.playing : audio && !audio.paused;
    if (!running) return;
    updateProgress();
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
}

function stopLoop() {
  if (raf) cancelAnimationFrame(raf);
  raf = null;
}

async function fetchDuration(hash, seq) {
  if (!hash) return;
  try {
    const resp = await fetch(`${API}/api/loop?id=${encodeURIComponent(hash)}`);
    const body = await resp.json();
    const d = Number(body?.data?.results?.duration);
    if (seq === durationSeq && isFinite(d) && d > 0) {
      metaDuration = d;
      updateLoaded();
    }
  } catch (e) {
    /* progress falls back to the element's duration once it is finite */
  }
}

// Silence and unload the element, so the server stops transcoding.
function detachElement() {
  if (!audio) return;
  if (!audio.paused) {
    quietPause = true;
    audio.pause();
  }
  audio.removeAttribute("src");
  try {
    audio.load();
  } catch (e) {
    /* nothing loaded */
  }
}

function cancelHandoff() {
  if (handoffTimer) clearTimeout(handoffTimer);
  handoffTimer = null;
  handoffPending = false;
  if (onVisible && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisible);
  }
  onVisible = null;
}

// The buffer sounds from here on: the element goes.
function finishHandoff(seq) {
  if (seq !== srcSeq || !handoffPending) return;
  cancelHandoff();
  owner = "buffer";
  detachElement();
  st.buffering = false;
  st.playing = gapless.playing;
  startLoop();
}

// Decoded: switch at the element's next wrap, or, paused, on resume. A
// hidden tab waits until it shows (its timers run late, and the element
// would sound under the buffer meanwhile).
function takeOver(seq) {
  if (seq !== srcSeq || !gapless || !gapless.ready || !st.loop || !audio) return;
  if (bufferOwns() || handoffPending) return;
  const ctx = gapless.context;
  if (typeof document !== "undefined" && document.hidden) {
    onVisible = () => {
      if (document.hidden) return;
      document.removeEventListener("visibilitychange", onVisible);
      onVisible = null;
      takeOver(seq);
    };
    document.addEventListener("visibilitychange", onVisible);
    return;
  }
  if (audio.paused) {
    gapless.pausedAt = (audio.currentTime || 0) % gapless.duration;
    owner = "buffer";
    detachElement();
    return;
  }
  if (ctx.state !== "running") return; // never unlocked: the stream keeps looping
  // Not sounding yet (the stream still buffering, or stalled): the buffer
  // starts now, from where the element is.
  if (st.buffering || !(audio.currentTime > 0)) {
    gapless.play(audio.currentTime || 0);
    owner = "buffer";
    detachElement();
    st.buffering = false;
    st.playing = true;
    startLoop();
    return;
  }
  // The element plays out its pass (no wrap, so no doubling if the timer
  // runs late) while the buffer starts on the wrap.
  audio.loop = false;
  handoffPending = true;
  const remaining = gapless.takeOverAt(audio.currentTime || 0);
  handoffTimer = setTimeout(() => finishHandoff(seq), Math.max(0, remaining * 1000));
}

function prepareGapless(url, seq) {
  const g = url && st.loop ? gaplessEngine() : null;
  if (!g) return;
  const ctx = g.context;
  // Unlock on this gesture (Safari and iOS start contexts suspended).
  if (ctx.state === "suspended" && typeof ctx.resume === "function") ctx.resume().catch(() => {});
  g.release();
  g.setLoop(true);
  g.load(url).then((ok) => {
    if (ok) takeOver(seq);
  });
}

function teardown() {
  stopLoop();
  cancelHandoff();
  if (gapless) gapless.release();
  owner = "element";
  if (audio) {
    audio.pause();
    // Detach the source so the browser aborts the stream (the server sees
    // the disconnect and stops transcoding).
    audio.removeAttribute("src");
    try {
      audio.load();
    } catch (e) {
      /* nothing loaded */
    }
  }
  st.playing = false;
  st.buffering = false;
  st.progress = 0;
  st.loaded = 0;
  metaDuration = 0;
  durationSeq += 1;
}

function ensureAudio() {
  if (audio) return audio;
  // The one <audio> the web plays previews through.
  audio = new Audio();
  audio.preload = "none";
  audio.addEventListener("playing", () => {
    st.buffering = false;
    st.playing = true;
    st.error = null;
    startLoop();
  });
  audio.addEventListener("waiting", () => {
    if (bufferOwns()) return;
    st.buffering = true;
  });
  audio.addEventListener("pause", () => {
    if (quietPause) {
      quietPause = false;
      return;
    }
    if (bufferOwns() || handoffPending) return;
    st.playing = false;
    st.buffering = false;
    stopLoop();
  });
  audio.addEventListener("ended", () => {
    // The pass before a takeover: the buffer is already sounding.
    if (handoffPending) {
      finishHandoff(srcSeq);
      return;
    }
    if (bufferOwns()) return;
    // Native loop repeats without an ended event: this is a one-pass play.
    passEnded();
  });
  audio.addEventListener("timeupdate", () => {
    if (!bufferOwns()) updateProgress();
  });
  audio.addEventListener("progress", updateLoaded);
  audio.addEventListener("error", () => {
    const src = audio.getAttribute("src") || "";
    if (!src) return;
    // A webm error means the canPlayType probe overpromised (Safari):
    // demote to AAC for the rest of the visit and retry this play once.
    if (bufferOwns() || handoffPending) return; // the element is done with
    if (src.includes("fmt=webm") && st.item) {
      demoteStreamFormat();
      const retry = src.replace("fmt=webm", "fmt=aac");
      startSrc(retry, { loop: st.loop, keepDuration: true, gapless: gaplessUrl });
      return;
    }
    if (fallbackUrl && src !== fallbackUrl) {
      const fb = fallbackUrl;
      fallbackUrl = null;
      startSrc(fb, { loop: st.loop, keepDuration: true, gapless: gaplessUrl });
      return;
    }
    console.error("Couldn't play this preview:", audio.error);
    st.buffering = false;
    st.playing = false;
    st.error = "Couldn't play this preview.";
  });
  return audio;
}

// One pass done (the element's end, or the buffer's in one-pass mode).
function passEnded() {
  st.playing = false;
  st.progress = 0;
  stopLoop();
  if (st.queue.length && st.index >= 0 && st.index < st.queue.length - 1 && !st.loop) {
    next();
    return;
  }
  stop();
}

function startSrc(src, { loop, keepDuration = false, gapless: seamless = null } = {}) {
  const keep = metaDuration;
  teardown();
  if (keepDuration) metaDuration = keep;
  gaplessUrl = seamless;
  const a = ensureAudio();
  a.loop = !!loop;
  a.src = src;
  st.src = src;
  st.loop = !!loop;
  st.buffering = true;
  const seq = ++srcSeq;
  a.play().catch(() => {
    if (seq !== srcSeq) return;
    st.buffering = false;
    st.playing = false;
  });
  prepareGapless(seamless, seq);
}

/**
 * Play `item` from `src`. `durationHint` (seconds) skips the metadata
 * fetch; `queue` and `index` let next and prev step through a list.
 */
export function play(
  item,
  { src, loop = true, durationHint = 0, queue = null, index = -1, fallback = null, gapless: seamless = null } = {},
) {
  if (!item || !src) return;
  nowPlaying.auditionStarted(item);
  st.id = item.id;
  st.item = item;
  st.error = null;
  if (Array.isArray(queue)) {
    st.queue = queue;
    st.index = index;
  } else if (!queue) {
    st.queue = [];
    st.index = -1;
  }
  fallbackUrl = fallback;
  startSrc(src, { loop, gapless: seamless });
  if (durationHint > 0) {
    metaDuration = durationHint;
  } else if (item.waveform && item.waveform.hash) {
    fetchDuration(item.waveform.hash, durationSeq);
  }
}

// The stream for a sound: the clean preview with a download token, else
// the watermarked one with the stream token (or none).
export function streamUrlFor(sound) {
  const token = (sound && (sound.downloadToken || sound.streamToken)) || null;
  return getStreamUrlFromProxyWithId(sound.hash, token);
}

// The padding-free file the seamless loop decodes: the 16-bit WAV when the
// sound is yours to download anyway (free, owned, bought), else Ogg Opus,
// watermarked as the stream is (a lossless preview of a paid sound would
// undercut buying it). Long loops keep the stream.
export function gaplessUrlFor(sound) {
  if (!sound || !sound.hash) return null;
  const clean = !!sound.downloadToken;
  const fmt = clean ? "wav" : "ogg";
  const cap = clean ? MAX_GAPLESS_SECONDS : MAX_GAPLESS_SECONDS * 2;
  if (Number(sound.duration) > cap) return null;
  const token = sound.downloadToken || sound.streamToken || null;
  const url = `${API}/download/stream/${encodeURIComponent(sound.hash)}?fmt=${fmt}`;
  return token ? `${url}&token=${encodeURIComponent(token)}` : url;
}

// The bar's item for a sound (Sounds, My audio, layer rows, the buy sheet).
// Left undefined, the extras follow the sound (MacSounds, Sounds):
//   addToSession  free, owned, or buyable and not yours (SI-25)
//   pricePill     a sound you'd have to buy: its chip, onto the buy sheet
//   share         otherwise the sound's link (the MiniPlayer's share)
// Pass null to leave one out.
export function soundItem(
  sound,
  { cta = undefined, color = null, addToSession = undefined, pricePill = undefined, share = undefined } = {}
) {
  const me = store.state.loggedInUser?.username || null;
  const owner = color || forName(sound.creator, me);
  const by = sound.creatorDisplay || sound.creator || "";
  const where =
    sound.sessionDisplay || (sound.packs && sound.packs[0] && sound.packs[0].title) || prettySessionName(sound.sessionName || "");
  const c = cta === undefined ? soundCta(sound) : cta;
  const buyable = !canDownload(sound) && canBuy(sound);
  const chipLabel = sound.chip && sound.chip.label;
  return {
    id: sound.hash,
    title: sound.title,
    // The app's order: where it lives, then who made it.
    subtitle: [where, by].filter(Boolean).join(" · "),
    art: { seed: sound.hash, owner, picture: null },
    color: owner,
    waveform: { hash: sound.hash },
    meta: [sound.key, sound.bpm != null ? `${Math.round(sound.bpm)} BPM` : null].filter(Boolean).join(" · "),
    keyBpm: keyBpmText(sound),
    sound,
    // Download and Buy are the bar's violet call to action (MacSounds).
    cta: c ? { ...c, kind: c.kind === "none" ? "tinted" : "primary", onClick: ctaAction(sound, c) } : null,
    addToSession:
      addToSession !== undefined ? addToSession : canAddToSession(sound) ? () => stageInNewSession(sound) : null,
    dragHandle: canDownload(sound) ? { sound, url: getWavDownloadFromProxyWithId(sound.hash, sound.downloadToken) } : null,
    // A sound you'd buy first keeps the grip's slot, empty (SI-11; the
    // app's SoundDragHandle "Buy to drag"), so the bar's buttons stay put.
    buyToDrag: buyable,
    pricePill:
      pricePill !== undefined
        ? pricePill
        : buyable && chipLabel
        ? { label: chipLabel, onClick: () => soundSheets.open("buy", sound) }
        : null,
    share: share !== undefined ? share : buyable ? null : () => shareLink(sound, me),
  };
}

// What the bar's Download or Buy does (soundCta's kind).
function ctaAction(sound, c) {
  if (c.kind === "download") return () => downloadSound(sound);
  if (c.kind === "buy") return () => soundSheets.open("buy", sound);
  return null;
}

/** Play a kit/sound.js Sound. `colorFor(sound)` colours the queue's
 * others when ⏮ ⏭ reach them (a session's rows). */
export function playSound(sound, { queue = null, index = -1, cta = undefined, loop = true, color = null, item = null, colorFor = null } = {}) {
  if (!sound || !sound.hash) return;
  const it = item || soundItem(sound, { cta, color });
  const q = Array.isArray(queue) ? queue : null;
  queueColorFor = q && typeof colorFor === "function" ? colorFor : null;
  const i = q ? (index >= 0 ? index : q.findIndex((s) => s && s.hash === sound.hash)) : -1;
  play(it, {
    src: streamUrlFor(sound),
    loop,
    durationHint: sound.duration || 0,
    queue: q,
    index: i,
    gapless: gaplessUrlFor(sound),
  });
}

/** Play a URL (Discover's mixdown); `fallbackUrl` is tried once on error,
 * and `gapless`, a padding-free copy, takes over at the stream's next wrap. */
export function playUrl(url, item, { fallbackUrl: fb = null, loop = true, durationHint = 0, gapless: seamless = null } = {}) {
  play(item, { src: url, loop, durationHint, fallback: fb, gapless: seamless });
}

export function pause() {
  if (bufferOwns()) {
    gapless.pause();
    st.playing = false;
    st.buffering = false;
    stopLoop();
    return;
  }
  if (handoffPending) {
    // Paused before the wrap: the buffer resumes where the element stopped.
    const at = audio ? audio.currentTime || 0 : 0;
    cancelHandoff();
    gapless.pause();
    gapless.pausedAt = at % gapless.duration;
    owner = "buffer";
    detachElement();
    st.playing = false;
    st.buffering = false;
    stopLoop();
    return;
  }
  if (audio) audio.pause();
}

export function resume() {
  if (bufferOwns() && st.src) {
    if (st.item) nowPlaying.auditionStarted(st.item);
    const ctx = gapless.context;
    if (ctx.state === "suspended" && typeof ctx.resume === "function") ctx.resume().catch(() => {});
    gapless.play(gapless.pausedAt);
    st.playing = true;
    st.buffering = false;
    startLoop();
    return;
  }
  if (!audio || !st.src) return;
  if (st.item) nowPlaying.auditionStarted(st.item);
  st.buffering = audio.readyState < 2;
  const seq = ++srcSeq;
  audio.play().catch(() => {
    if (seq === srcSeq) st.buffering = false;
  });
}

export function toggle() {
  if (st.playing || st.buffering) pause();
  else resume();
}

// Stop and unload. { silent } skips handing the bar back (Play all is
// taking it).
export function stop({ silent = false } = {}) {
  const had = !!st.item;
  teardown();
  st.id = null;
  st.item = null;
  st.src = null;
  st.queue = [];
  st.index = -1;
  fallbackUrl = null;
  queueColorFor = null;
  if (had && !silent) nowPlaying.auditionEnded();
}

export function seek(frac) {
  if (bufferOwns()) {
    gapless.seek(frac);
    updateProgress();
    return;
  }
  if (handoffPending) {
    // A jump re-aims the wrap: start over from the new spot.
    cancelHandoff();
    gapless.pause();
    audio.loop = st.loop;
  }
  const d = effectiveDuration();
  if (!audio || d <= 0) return;
  try {
    audio.currentTime = Math.max(0, Math.min(1, frac)) * d;
    updateProgress();
  } catch (e) {
    /* not seekable yet (a cold chunked stream) */
  }
  if (gapless && gapless.ready) takeOver(srcSeq);
}

export function setLoop(loop) {
  st.loop = !!loop;
  if (bufferOwns()) {
    gapless.setLoop(loop);
    return;
  }
  if (handoffPending && !loop) {
    // One pass: the element finishes it; no takeover.
    cancelHandoff();
    gapless.pause();
  }
  if (audio) audio.loop = !!loop;
}

// ⏮ / ⏭: clamped at the queue's ends, as the app's step (queueStep.js).
function step(delta) {
  const at =
    st.index >= 0 && st.queue[st.index] && st.queue[st.index].hash === st.id
      ? st.index
      : st.queue.findIndex((s) => s && s.hash === st.id);
  const i = stepTarget(at, st.queue.length, delta < 0);
  if (i < 0) return;
  const sound = st.queue[i];
  if (!sound || !sound.hash) return;
  const colorFor = queueColorFor;
  playSound(sound, { queue: st.queue, index: i, loop: st.loop, color: colorFor ? colorFor(sound) : null, colorFor });
  nowPlaying.auditionUpdated(st.item);
}
export function next() {
  step(1);
}
export function prev() {
  step(-1);
}

export const isCurrent = (id) => !!id && st.id === id;
export const isPlaying = (id) => isCurrent(id) && st.playing;
export const isBuffering = (id) => isCurrent(id) && st.buffering;
export const progressOf = (id) => (isCurrent(id) ? st.progress : 0);
export const loadedOf = (id) => (isCurrent(id) ? st.loaded : 0);

nowPlaying.registerEngine("audition", {
  playing: () => st.playing,
  buffering: () => st.buffering,
  progress: () => st.progress,
  loaded: () => st.loaded,
  // As the app's bar: ⏮ and ⏭ stay live for any sound or list, and do
  // nothing past the queue's ends (step() stops there). Discover's
  // mixdown, with no list behind it, has neither.
  canPrev: () => canStep({ queueLength: st.queue.length, isSound: !!(st.item && st.item.sound) }),
  canNext: () => canStep({ queueLength: st.queue.length, isSound: !!(st.item && st.item.sound) }),
  toggle,
  pause,
  resume,
  stop,
  // Signing out: stop, keep no queue, and leave the bar to stopAll.
  unload: () => stop({ silent: true }),
  seek,
  next,
  prev,
});

const audition = {
  state: st,
  play,
  playSound,
  playUrl,
  soundItem,
  streamUrlFor,
  gaplessUrlFor,
  toggle,
  pause,
  resume,
  stop,
  seek,
  setLoop,
  next,
  prev,
  isCurrent,
  isPlaying,
  isBuffering,
  progressOf,
  loadedOf,
};
export default audition;
